import { useState } from 'react'
import emailjs from '@emailjs/browser'
import './Contact.css'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState('')
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
    
    // 입력하면 에러 메시지 제거
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // 이름 체크
    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.'
    }

    // 이메일 체크
    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식을 입력해주세요.'
    }

    // 문의 내용 체크
    if (!formData.message.trim()) {
      newErrors.message = '문의 내용을 입력해주세요.'
    } else if (formData.message.trim().length < 10) {
      newErrors.message = '문의 내용을 10자 이상 입력해주세요.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation 체크
    if (!validateForm()) {
      setSubmitStatus('validation-error')
      return
    }

    setIsSubmitting(true)
    setSubmitStatus('')

    try {
      // EmailJS 서비스로 이메일 발송
      const result = await emailjs.send(
        'service_n6254wc', // EmailJS 서비스 ID
        'template_e1sgao3', // EmailJS 템플릿 ID
        {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company || '(미입력)',
          phone: formData.phone || '(미입력)',
          inquiry_type: formData.inquiryType || '(미선택)',
          message: formData.message,
          to_email: 'ceo@nexpa-ai.com'
        },
        '2rEFXLbQPqD_BmvrE' // EmailJS 공개 키
      )

      console.log('이메일 발송 성공:', result.text)
      setSubmitStatus('success')
      
      // 폼 초기화
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        inquiryType: '',
        message: ''
      })
      setErrors({})
    } catch (error) {
      console.error('이메일 발송 실패:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="hero-content fade-in-up">
            <h1>문의하기</h1>
            <p className="hero-subtitle">
              NEXPA와 함께 문해력 교육의 미래를 만들어가세요
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info" style={{padding: '0'}}>
              <h2>연락처 정보</h2>
              
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">🏢</div>
                  <div className="contact-text">
                    <h4>회사명</h4>
                    <p>주식회사 넥스파 (NEXPA Inc.)</p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">📍</div>
                  <div className="contact-text">
                    <h4>주소</h4>
                    <p>서울시 서초구 강남대로 107길 21, 2층</p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">📞</div>
                  <div className="contact-text">
                    <h4>전화</h4>
                    <p>02-6207-7400</p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">✉️</div>
                  <div className="contact-text">
                    <h4>이메일</h4>
                    <p>ceo@nexpa-ai.com</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="contact-form">
              <h2>문의 양식</h2>
              <form className="form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">이름 *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                    required 
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="company">회사/기관명</label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company" 
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">이메일 *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    required 
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">연락처</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="inquiryType">문의 유형</label>
                  <select 
                    id="inquiryType" 
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                  >
                    <option value="">선택해주세요</option>
                    <option value="partnership">파트너십 문의</option>
                    <option value="demo">데모 요청</option>
                    <option value="trial">무료 체험</option>
                    <option value="business">사업 협력</option>
                    <option value="general">일반 문의</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">문의 내용 *</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="6" 
                    value={formData.message}
                    onChange={handleChange}
                    className={errors.message ? 'error' : ''}
                    placeholder="문의 내용을 자세히 작성해주세요. (최소 10자 이상)"
                    required
                  ></textarea>
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>
                
                <button 
                  type="submit" 
                  className={`btn btn-primary ${isSubmitting ? 'submitting' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '발송 중...' : '문의하기'}
                </button>

                {/* 상태 메시지 */}
                {submitStatus === 'success' && (
                  <div className="submit-message success">
                    ✅ 문의가 성공적으로 발송되었습니다! 빠른 시일 내에 연락드리겠습니다.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="submit-message error">
                    ❌ 문의 발송에 실패했습니다. 다시 시도해주세요.
                  </div>
                )}

                {submitStatus === 'validation-error' && (
                  <div className="submit-message error">
                    ⚠️ 필수 항목을 확인해주세요.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="partnership-types">
        <div className="container">
          <h2 className="section-title">PARTNERSHIP 안내</h2>
          
          <div className="partnership-grid">
            <div className="partnership-card card">
              <div className="partnership-icon">🤝</div>
              <h3>TECHNICAL PARTNERSHIP</h3>
              <p>AI 기술 개발 및 플랫폼 고도화를 위한 기술 협력 파트너를 찾습니다.</p>
              <ul>
                <li>AI/ML 기술 협력</li>
                <li>플랫폼 개발 협력</li>
                <li>기술 자문 및 컨설팅</li>
              </ul>
            </div>
            
            <div className="partnership-card card">
              <div className="partnership-icon">📚</div>
              <h3>CONTENTS PARTNERSHIP</h3>
              <p>풍부하고 질 높은 교육 콘텐츠 제공을 위한 출판사 및 콘텐츠 제작사와의 협력</p>
              <ul>
                <li>출판사 제휴</li>
                <li>교육 콘텐츠 제공</li>
                <li>저작권 라이선싱</li>
              </ul>
            </div>
            
            <div className="partnership-card card">
              <div className="partnership-icon">🏫</div>
              <h3>EDUCATION PARTNERSHIP</h3>
              <p>학교, 학원, 교육센터 등과의 협력을 통한 플랫폼 확산 및 실증</p>
              <ul>
                <li>파일럿 프로그램 운영</li>
                <li>커리큘럼 연계</li>
                <li>교육 효과 검증</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">자주 묻는 질문</h2>
          
          <div className="faq-content">
            <div className="faq-item">
              <h4>LIDIA 시스템은 어떤 연령대를 대상으로 하나요?</h4>
              <p>LIDIA는 초등학생부터 성인까지 전 연령대를 대상으로 합니다. 각 연령별 맞춤형 버전(Elementary, Teen, Classic 등)을 제공하여 최적화된 평가를 진행합니다.</p>
            </div>
            
            <div className="faq-item">
              <h4>무료 체험이 가능한가요?</h4>
              <p>네, 무료 체험을 제공합니다. 문의 양식을 통해 체험 신청을 하시면 담당자가 연락드려 상세한 안내를 해드립니다.</p>
            </div>
            
            <div className="faq-item">
              <h4>기업 및 교육기관 대상 서비스도 제공하나요?</h4>
              <p>네, 학교, 학원, 기업 교육 프로그램 등 다양한 기관을 대상으로 한 B2B 서비스를 제공합니다. 대량 라이선스 및 맞춤형 솔루션이 가능합니다.</p>
            </div>
            
            <div className="faq-item">
              <h4>해외 진출 계획이 있나요?</h4>
              <p>글로벌 확산을 목표로 다국어 평가 콘텐츠를 구축하고 있습니다. 해외 파트너십에 관심있는 기관은 언제든 문의해주세요.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact 